import React, { useState, useRef } from 'react'
import { Modal, message, Divider, Button, Spin, Progress, Select } from 'antd'
import { LoadingOutlined } from '@ant-design/icons';
import { RiFileVideoLine, RiFileSettingsLine } from "react-icons/ri";
import { FiAlertCircle } from "react-icons/fi";
import { MdOutlineDone } from "react-icons/md";
import { TextField } from "@mui/material";
import { prepareData } from './dataPreparation';
import AuthContext from '../contexts/AuthContext';
import { storage } from '../firebase';
//import { uploadData } from 'aws-amplify/storage';
import axios from 'axios';
import { getDistance } from 'geolib';

// Utility function to calculate geodesic distance between two GPS coordinates using geolib
// This is more accurate than Haversine formula, similar to geopy's geodesic
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    try {
        // getDistance returns distance in meters, convert to kilometers
        const distanceInMeters = getDistance(
            { latitude: lat1, longitude: lon1 },
            { latitude: lat2, longitude: lon2 },
            1 // accuracy (1 meter)
        );
        return distanceInMeters / 1000; // Convert to kilometers
    } catch (error) {
        console.error('Error calculating distance:', error);
        return Infinity; // Return infinity if calculation fails
    }
};

// Function to calculate sub_road_id by arranging videos in sequence based on GPS proximity
// This function works with both new and existing videos to maintain proper sequencing
const calculateSubRoadIds = (videos) => {
    if (!videos || videos.length === 0) return [];
    if (videos.length === 1) {
        return [{ ...videos[0], sub_road_id: 1 }];
    }

    // Create a copy of videos array with parsed coordinates
    const videosWithIndex = videos.map((video, index) => ({
        ...video,
        originalIndex: index,
        startLat: parseFloat(video.startLat || video.start_latitude),
        startLon: parseFloat(video.startLon || video.start_longitude),
        endLat: parseFloat(video.endLat || video.end_latitude),
        endLon: parseFloat(video.endLon || video.end_longitude)
    }));

    // Sort videos to create a sequential path using geodesic distance
    const sortedVideos = [];
    const remaining = [...videosWithIndex];

    // Start with the first video
    sortedVideos.push(remaining.shift());

    // Sequentially add videos that are closest to the end of the last video in the sequence
    while (remaining.length > 0) {
        const lastVideo = sortedVideos[sortedVideos.length - 1];
        let minDistance = Infinity;
        let closestIndex = 0;

        // Find the closest video to the end point of the last video
        remaining.forEach((video, index) => {
            // Calculate geodesic distance from last video's end to this video's start
            const distanceToStart = calculateDistance(
                lastVideo.endLat, lastVideo.endLon,
                video.startLat, video.startLon
            );

            // Also consider distance from last video's end to this video's end (reverse direction)
            const distanceToEnd = calculateDistance(
                lastVideo.endLat, lastVideo.endLon,
                video.endLat, video.endLon
            );

            // Use the minimum distance
            const distance = Math.min(distanceToStart, distanceToEnd);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
            }
        });

        sortedVideos.push(remaining.splice(closestIndex, 1)[0]);
    }

    // Assign sub_road_id based on the sorted order
    return sortedVideos.map((video, index) => ({
        ...video,
        sub_road_id: index + 1
    }));
};

// Function to fetch existing videos for a given road_name from the backend
const fetchExistingVideosForRoad = async (roadName, token) => {
    try {
        if (!roadName || !roadName.trim()) {
            return [];
        }

        const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}dataUpload/get_videos_by_road_name`,
            {
                params: { road_name: roadName.trim() },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Existing videos for road:', roadName, response.data);
        return response.data.videos || response.data.result || [];
    } catch (error) {
        console.error('Error fetching existing videos:', error);
        // If endpoint doesn't exist or fails, return empty array
        return [];
    }
};

// Function to update sub_road_ids for existing videos in the database
const updateSubRoadIdsInDatabase = async (videosToUpdate, token) => {
    try {
        if (!videosToUpdate || videosToUpdate.length === 0) {
            return;
        }

        // Prepare update payload with uid and new sub_road_id
        const updates = videosToUpdate.map(video => ({
            uid: video.uid || video.video_uid,
            sub_road_id: video.sub_road_id
        }));

        console.log('Updating sub_road_ids in database:', updates);

        await axios.post(
            `${process.env.REACT_APP_BASE_URL}dataUpload/update_sub_road_ids`,
            { updates },
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Successfully updated sub_road_ids in database');
    } catch (error) {
        console.error('Error updating sub_road_ids:', error);
        throw error;
    }
};


const Upload = ({ visible, onClose, defaultRoadId }) => {
    const { user } = React.useContext(AuthContext);
    // Multiple selection state
    const [videoFiles, setVideoFiles] = useState([]); // File[]
    const [gpxFiles, setGpxFiles] = useState([]); // File[] corresponding by index to videoFiles
    const [perFileCoords, setPerFileCoords] = useState([]); // [{startLat, startLon, endLat, endLon, gpxName}]
    const [roadId, setRoadId] = useState("");
    const [roadName, setRoadName] = useState("");
    const [startLatitude, setStartLatitude] = useState("");
    const [startLongitude, setStartLongitude] = useState("");
    const [endLatitude, setEndLatitude] = useState("");
    const [endLongitude, setEndLongitude] = useState("");
    const [roadType, setRoadType] = useState("LHS");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [preparedVideoFile, setPreparedVideoFile] = useState(null); // kept for backward compat (single display)
    const [preparedGpxFile, setPreparedGpxFile] = useState(null);
    const [preparedItems, setPreparedItems] = useState([]); // [{uid, data, video: File, gpx: File, index}]
    const [isPrepared, setIsPrepared] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [perFileProgress, setPerFileProgress] = useState([]); // number[] 0-100 per file
    const [abortController, setAbortController] = useState(new AbortController());
    const [uid, setUid] = useState("");
    const [data, setData] = useState(null);
    const videoInputRef = useRef(null);
    const gpxInputRef = useRef(null);
    const [isConverting, setIsConverting] = useState(false);
    const [roadDatabase, setRoadDatabase] = useState([]);
    const [loadingRoads, setLoadingRoads] = useState(false);
    const [existingVideosToUpdate, setExistingVideosToUpdate] = useState([]); // Track existing videos that need sub_road_id update

    // Fetch road database when modal opens
    React.useEffect(() => {
        if (visible) {
            fetchRoadDatabase();
        }
    }, [visible]);

    // Pre-select road when defaultRoadId is provided and roads are loaded
    React.useEffect(() => {
        if (defaultRoadId && roadDatabase.length > 0) {
            const match = roadDatabase.find(r => r.road_id === defaultRoadId);
            if (match) {
                setRoadId(match.road_id);
                setRoadName(match.road_name || '');
                setRoadType(match.road_type || 'LHS');
            }
        }
    }, [defaultRoadId, roadDatabase]);

    const fetchRoadDatabase = async () => {
        try {
            setLoadingRoads(true);
            const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}dataUpload/get_excel_data`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const roads = response.data.result || [];

            console.log('Fetched road database from API:', roads);
            console.log('Sample road data:', roads[0]);

            // Filter only roads that have road_id
            const validRoads = roads.filter(road => road.road_id);
            setRoadDatabase(validRoads);

            console.log(`Loaded ${validRoads.length} roads with Road IDs`);
        } catch (error) {
            console.error('Error fetching road database:', error);
            message.error('Failed to load road database');
        } finally {
            setLoadingRoads(false);
        }
    };

    const handleVideoFileSelect = () => {
        if (videoInputRef.current) {
            videoInputRef.current.click();
        }
    };

    const handleGpxFileSelect = () => {
        if (gpxInputRef.current) {
            gpxInputRef.current.click();
            if (errorMessage.includes("GPX file")) {
                setErrorMessage("");
            }
        }
    };

    const handleVideoFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 50) {
            message.error("You can upload up to 50 videos at a time");
        }
        const limited = files.slice(0, 50);

        setVideoFiles(limited);
        message.success(`Selected ${limited.length} video(s)`);
    };

    const parseSingleGpx = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    let gpxContent = event.target.result;
                    if (typeof gpxContent === 'string' && gpxContent.startsWith('<? xml')) {
                        gpxContent = gpxContent.replace('<? xml', '<?xml').replace(/\s+=\s+/g, '=').replace(/\s+\?>/, '?>');
                    }
                    const parser = new DOMParser();
                    let xmlDoc = parser.parseFromString(gpxContent, 'text/xml');
                    const errorNode = xmlDoc.getElementsByTagName('parsererror');
                    if (errorNode.length > 0) {
                        throw new Error('Invalid GPX XML format');
                    }
                    let gpxElement = xmlDoc.getElementsByTagName('gpx')[0];
                    let trkptElements = xmlDoc.getElementsByTagNameNS('*', 'trkpt');
                    if (!gpxElement || trkptElements.length === 0) {
                        const sampleGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                        xmlDoc = parser.parseFromString(sampleGpx, 'text/xml');
                        gpxElement = xmlDoc.getElementsByTagName('gpx')[0];
                        trkptElements = xmlDoc.getElementsByTagNameNS('*', 'trkpt');
                    } else {
                        gpxElement.setAttribute('version', '1.1');
                        gpxElement.setAttribute('xmlns', 'http://www.topografix.com/GPX/1/1');
                        gpxElement.setAttribute('xmlns:gpxtpx', 'http://www.garmin.com/xmlschemas/TrackPointExtension/v1');
                        Array.from(trkptElements).forEach((trkpt) => {
                            if (trkpt && trkpt.nodeType === 1) {
                                if (!trkpt.hasAttribute('lat')) trkpt.setAttribute('lat', '26.276730');
                                if (!trkpt.hasAttribute('lon')) trkpt.setAttribute('lon', '73.001525');
                                const timeTags = trkpt.getElementsByTagName('time');
                                if (timeTags.length === 0) {
                                    const timeEl = xmlDoc.createElement('time');
                                    timeEl.textContent = new Date().toISOString();
                                    trkpt.appendChild(timeEl);
                                }
                                const eleTags = trkpt.getElementsByTagName('ele');
                                if (eleTags.length === 0) {
                                    const eleEl = xmlDoc.createElement('ele');
                                    eleEl.textContent = '0';
                                    trkpt.appendChild(eleEl);
                                }
                            }
                        });
                    }
                    let coords = null;
                    if (trkptElements.length > 0) {
                        const firstPoint = trkptElements[0];
                        const lastPoint = trkptElements[trkptElements.length - 1];
                        coords = {
                            startLat: firstPoint.getAttribute('lat') || '',
                            startLon: firstPoint.getAttribute('lon') || '',
                            endLat: lastPoint.getAttribute('lat') || '',
                            endLon: lastPoint.getAttribute('lon') || ''
                        };
                    }
                    const serializer = new XMLSerializer();
                    const correctedGpxContent = serializer.serializeToString(xmlDoc);
                    const correctedBlob = new Blob([correctedGpxContent], { type: 'application/gpx+xml' });
                    const correctedFile = new File([correctedBlob], file.name, { type: 'application/gpx+xml' });
                    resolve({ correctedFile, coords });
                } catch (err) {
                    const fallbackGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                    const fallbackBlob = new Blob([fallbackGpx], { type: 'application/gpx+xml' });
                    const fallbackFile = new File([fallbackBlob], file.name, { type: 'application/gpx+xml' });
                    resolve({ correctedFile: fallbackFile, coords: { startLat: '26.276730', startLon: '73.001525', endLat: '26.276730', endLon: '73.001525' } });
                }
            };
            reader.onerror = () => {
                const fallbackGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                const fallbackBlob = new Blob([fallbackGpx], { type: 'application/gpx+xml' });
                const fallbackFile = new File([fallbackBlob], file.name, { type: 'application/gpx+xml' });
                resolve({ correctedFile: fallbackFile, coords: { startLat: '26.276730', startLon: '73.001525', endLat: '26.276730', endLon: '73.001525' } });
            };
            reader.readAsText(file);
        });
    };

    const handleGpxFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 50) {
            message.error("You can upload up to 50 GPX files at a time");
        }
        const limited = files.slice(0, 50);
        setIsConverting(true);
        if (errorMessage.includes("GPX file")) {
            setErrorMessage("");
        }
        const results = await Promise.all(limited.map(parseSingleGpx));
        const corrected = results.map(r => r.correctedFile);
        const coords = results.map((r, idx) => ({ ...r.coords, gpxName: limited[idx]?.name }));
        setGpxFiles(corrected);
        setPerFileCoords(coords);
        // For convenience, set single fields with the first GPX's coords
        if (coords[0]) {
            setStartLatitude(coords[0].startLat || '');
            setStartLongitude(coords[0].startLon || '');
            setEndLatitude(coords[0].endLat || '');
            setEndLongitude(coords[0].endLon || '');
        }
        message.success(`Processed ${corrected.length} GPX file(s)`);
        setIsConverting(false);
    };
    
    const handleRoadNameSelect = (value) => {
        setRoadName(value);

        // Auto-fill Road ID and Road Type from the selected road name
        const selectedRoad = roadDatabase.find(road => road.road_name === value);
        if (selectedRoad) {
            setRoadId(selectedRoad.road_id || '');
            setRoadType(selectedRoad.road_type || 'LHS');

            console.log('Auto-filled road details from Road Name:', {
                road_name: value,
                road_id: selectedRoad.road_id,
                road_type: selectedRoad.road_type
            });

            message.info('Road ID and type auto-filled from database. Upload GPX file to fill coordinates.');
        }
    };

    const handleCancel = () => {
        setVideoFiles([]);
        setGpxFiles([]);
        setPerFileCoords([]);
        setRoadId("");
        setRoadName("");
        setStartLatitude("");
        setStartLongitude("");
        setEndLatitude("");
        setEndLongitude("");
        setRoadType("LHS");
        setPreparedVideoFile(null);
        setPreparedGpxFile(null);
        setPreparedItems([]);
        setExistingVideosToUpdate([]);
        setErrorMessage("");
        setIsPrepared(false);
        onClose();
    };


    const uploadVideoFile = async (presignedUrl, file, onProgress) => {
        try {
            await axios.put(presignedUrl, file, {
                headers: {
                    'Content-Type': 'video/mp4',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    onProgress(progress);
                },
                signal: abortController.signal,
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Upload paused');
            } else {
                console.error('Error during video upload:', error);
            }
        }
    };

    const handleSubmit = async () => {
        if (isPrepared) {
            // Upload phase
            setIsUploading(true);
            setLoading(true);
            try {
                const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
                console.log('Authorization Token:', token);
                if (!token) {
                    setErrorMessage('Authorization token is missing. Please login again.');
                    return;
                }
                // Initialize per-file progress tracking
                setPerFileProgress(Array(preparedItems.length).fill(0));

                // Upload videos sequentially (one by one) instead of in parallel
                for (let index = 0; index < preparedItems.length; index++) {
                    const item = preparedItems[index];

                    console.log(`📤 Uploading video ${index + 1} of ${preparedItems.length}: ${item.video?.name}`);

                    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}dataUpload/generate-presigned-urls`,
                        {
                            videoFileName: `public/${item.uid}/file.mp4`,
                            dataFileName: `public/${item.uid}/data.txt`,
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );
                    const { videoPresignedUrl, dataPresignedUrl } = response.data;
                    if (!videoPresignedUrl || !dataPresignedUrl) {
                        throw new Error('Failed to generate presigned URLs');
                    }

                    // Upload data.txt
                    const dataTxtContent = JSON.stringify(item.data, null, 2);
                    const dataUploadResponse = await fetch(dataPresignedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'text/plain' },
                        body: dataTxtContent,
                    });
                    if (!dataUploadResponse.ok) {
                        throw new Error('Failed to upload data.txt file');
                    }

                    // Upload video with per-file progress → aggregate to overall
                    await uploadVideoFile(videoPresignedUrl, item.video, (p) => {
                        setPerFileProgress((prev) => {
                            const next = [...prev];
                            next[index] = p;
                            // Calculate overall progress based on completed files + current file progress
                            const completedFiles = index;
                            const currentFileProgress = p / 100;
                            const overallProgress = Math.round(((completedFiles + currentFileProgress) / preparedItems.length) * 100);
                            setUploadProgress(overallProgress);
                            return next;
                        });
                    });

                    // Save metadata with sub_road_id
                    const videoMetadata = {
                        uid: item.uid,
                        road_id: roadId || null,
                        sub_road_id: item.sub_road_id || 1, // Include calculated sub_road_id
                        project_title: roadName,
                        starting_address: item.data.startAddress,
                        ending_address: item.data.endAddress,
                        start_latitude: item.data.startLatitude,
                        start_longitude: item.data.startLongitude,
                        end_latitude: item.data.endLatitude,
                        end_longitude: item.data.endLongitude,
                        road_type: roadType || item.data.roadType,
                        created: item.data.createdAt,
                        meta_data: {
                            videoLength: item.data.videoLength,
                            fileSize: item.data.fileSize
                        }
                    };

                    console.log('📤 Uploading video metadata with sub_road_id:', {
                        uid: videoMetadata.uid,
                        sub_road_id: videoMetadata.sub_road_id,
                        project_title: videoMetadata.project_title
                    });

                    await axios.post(`${process.env.REACT_APP_BASE_URL}dataUpload/video_upload`, videoMetadata, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        }
                    });

                    console.log(`✅ Video ${index + 1} of ${preparedItems.length} uploaded successfully`);
                }
                message.success(`Uploaded ${preparedItems.length} file(s) successfully`);

                // Update sub_road_ids for existing videos if needed
                if (existingVideosToUpdate.length > 0) {
                    try {
                        console.log('Updating sub_road_ids for existing videos...');
                        await updateSubRoadIdsInDatabase(existingVideosToUpdate, token);
                        message.success(`Updated sequence for ${existingVideosToUpdate.length} existing video(s)`);
                    } catch (error) {
                        console.error('Error updating existing videos:', error);
                        message.warning('New videos uploaded, but failed to update sequence for existing videos');
                    }
                }

                handleCancel();
            } catch (error) {
                console.error("Error during upload:", error);
                setErrorMessage(`Error during upload: ${error.message}`);
            } finally {
                setLoading(false);
                setIsUploading(false);
            }
        } else {
            // Preparation phase - Only video file is required
            const missingFields = [];
            if (!videoFiles || videoFiles.length === 0) missingFields.push("Video files");
            if (!gpxFiles || gpxFiles.length === 0) missingFields.push("GPX files");

            if (missingFields.length > 0) {
                setErrorMessage(`${missingFields.join(", ")} is required`);
                return;
            }
            if (videoFiles.length !== gpxFiles.length) {
                setErrorMessage("Videos count must match GPX count");
                return;
            }
            if (videoFiles.length > 50 || gpxFiles.length > 50) {
                setErrorMessage("Maximum 50 videos and 50 GPX files at a time");
                return;
            }
            setErrorMessage("");
            setLoading(true); // Set loading state
            try {
                const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
                const defaultRoadName = roadName.trim() || `Road_${new Date().getTime()}`;

                // Fetch existing videos for this road name
                console.log('Fetching existing videos for road:', defaultRoadName);
                const existingVideos = await fetchExistingVideosForRoad(defaultRoadName, token);
                console.log(`Found ${existingVideos.length} existing video(s) for this road`);

                const prepared = [];
                for (let i = 0; i < videoFiles.length; i++) {
                    const video = videoFiles[i];
                    const gpx = gpxFiles[i];
                    // Pull per-file coords from parsed GPX
                    const coords = perFileCoords[i] || {};
                    const { data: d, uid: u } = await prepareData(
                        video,
                        gpx,
                        defaultRoadName,
                        user.email,
                        user.name,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        roadType,
                        coords.startLat || startLatitude,
                        coords.startLon || startLongitude,
                        coords.endLat || endLatitude,
                        coords.endLon || endLongitude
                    );
                    prepared.push({
                        uid: u,
                        data: d,
                        video,
                        gpx,
                        index: i,
                        startLat: coords.startLat || startLatitude,
                        startLon: coords.startLon || startLongitude,
                        endLat: coords.endLat || endLatitude,
                        endLon: coords.endLon || endLongitude,
                        isNew: true // Mark as new video
                    });
                }

                // Combine existing videos with new videos for sequencing
                const allVideos = [
                    ...existingVideos.map(v => ({
                        ...v,
                        isNew: false,
                        uid: v.uid || v.video_uid
                    })),
                    ...prepared
                ];

                console.log('Total videos for sequencing:', allVideos.length);

                // Calculate sub_road_id based on GPS proximity sequencing for ALL videos
                const allVideosWithSubRoadIds = calculateSubRoadIds(allVideos);

                // Separate new and existing videos after recalculation
                const newVideosWithSubRoadIds = allVideosWithSubRoadIds.filter(v => v.isNew);
                const existingVideosWithNewSubRoadIds = allVideosWithSubRoadIds.filter(v => !v.isNew);

                console.log('New videos with sub_road_ids:', newVideosWithSubRoadIds);
                console.log('Existing videos with updated sub_road_ids:', existingVideosWithNewSubRoadIds);

                // Check if any existing videos need sub_road_id updates
                const existingVideosNeedUpdate = existingVideosWithNewSubRoadIds.filter((video, idx) => {
                    const originalVideo = existingVideos.find(v => (v.uid || v.video_uid) === video.uid);
                    return originalVideo && originalVideo.sub_road_id !== video.sub_road_id;
                });

                if (existingVideosNeedUpdate.length > 0) {
                    console.log(`${existingVideosNeedUpdate.length} existing video(s) need sub_road_id update`);
                    message.info(`Updating sequence for ${existingVideosNeedUpdate.length} existing video(s)...`);
                    setExistingVideosToUpdate(existingVideosNeedUpdate);
                } else {
                    setExistingVideosToUpdate([]);
                }

                setPreparedItems(newVideosWithSubRoadIds);
                setPerFileProgress(Array(newVideosWithSubRoadIds.length).fill(0));

                if (newVideosWithSubRoadIds[0]) {
                    setUid(newVideosWithSubRoadIds[0].uid);
                    setData(newVideosWithSubRoadIds[0].data);
                    setPreparedVideoFile(newVideosWithSubRoadIds[0].data.filePath);
                    setPreparedGpxFile('data.txt');
                }

                setIsPrepared(true);
                const totalVideos = allVideosWithSubRoadIds.length;
                message.success(`Prepared ${newVideosWithSubRoadIds.length} new video(s). Total sequence: ${totalVideos} video(s). Ready to upload.`);
            } catch (error) {
                setErrorMessage(`Error during preparation: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };


    return (
        <Modal
            title={'Upload Road Imagery'}
            centered
            closable={true}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width="35vw"
            modal
        >
            <>
                <div className='flex flex-col'>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Upload Video File
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Your selected video file must contain road imagery
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#0A2540', fontWeight: '500', marginBottom: 6, backgroundColor: '#F0F5FE', padding: '4px 8px', borderRadius: 4, width: 'fit-content' }}>
                            Supported format: .MP4
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div
                                onClick={!isPrepared ? handleVideoFileSelect : null}
                                style={{
                                    width: 'fit-content', borderRadius: 8, display: 'flex', alignItems: 'center',
                                    marginTop: 4, backgroundColor: 'var(--primary-color)', color: '#FFFFFF', padding: '6px 12px',
                                    cursor: !isPrepared ? 'pointer' : 'default', fontSize: '0.875rem', fontWeight: '500'
                                }}
                            >
                                <RiFileVideoLine style={{ marginRight: 6 }} />
                                {preparedVideoFile || (videoFiles.length > 0 ? 'Reselect video files' : 'Select video files from storage')}
                            </div>
                            {(preparedVideoFile || videoFiles.length > 0) && (
                                <div style={{ marginLeft: 10, marginTop: 4, fontSize: '0.85rem', color: '#5F6368' }}>
                                    {preparedVideoFile || `${videoFiles.length} video(s) selected`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Upload GPX File
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Your selected GPX file must contain GPS data. If not provided, a default GPS track will be used.
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#0A2540', fontWeight: '500', marginBottom: 6, backgroundColor: '#F0F5FE', padding: '4px 8px', borderRadius: 4, width: 'fit-content' }}>
                            Supported format: .GPX (GPS Exchange Format)
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div
                                onClick={!isPrepared && !isConverting ? handleGpxFileSelect : null}
                                style={{
                                    width: 'fit-content', borderRadius: 8, display: 'flex', alignItems: 'center',
                                    marginTop: 4, backgroundColor: 'var(--primary-color)', color: '#FFFFFF', padding: '6px 12px',
                                    cursor: !isPrepared && !isConverting ? 'pointer' : 'default', fontSize: '0.875rem', fontWeight: '500'
                                }}
                            >
                                <RiFileSettingsLine style={{ marginRight: 6 }} />
                                {isConverting ? (
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <Spin size="small" style={{ marginRight: 8 }} />
                                        Converting GPX file...
                                    </span>
                                ) : (
                                    preparedGpxFile || (gpxFiles.length > 0 ? 'Reselect GPX files' : 'Select GPX files from storage')
                                )}
                            </div>
                            {(preparedGpxFile || gpxFiles.length > 0) && !isConverting && (
                                <div style={{ marginLeft: 10, marginTop: 4, fontSize: '0.85rem', color: '#5F6368' }}>
                                    {preparedGpxFile || `${gpxFiles.length} GPX file(s) selected`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Road Name
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            This will auto-fill Road ID and Road Type
                        </p>
                        <Select
                            showSearch
                            value={roadName || undefined}
                            onChange={handleRoadNameSelect}
                            disabled={isPrepared || loadingRoads}
                            placeholder={loadingRoads ? "Loading roads..." : "Select a Road Name"}
                            style={{
                                width: '100%',
                                height: '40px',
                                backgroundColor: '#F0F5FE'
                            }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {roadDatabase.map((road) => (
                                <Select.Option key={road.road_id} value={road.road_name}>
                                    {road.road_name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Road ID
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Auto-detected from selected Road Name
                        </p>
                        <TextField
                            type="text"
                            name="roadId"
                            fullWidth
                            placeholder="Road ID"
                            value={roadId}
                            disabled={true}
                            variant="outlined"
                            style={{ backgroundColor: '#E5E7EB', width: '100%', borderColor: '#0071E3', borderRadius: 8 }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 40,
                                    borderRadius: 2,
                                },
                                '& .MuiInputBase-input': {
                                    padding: '8px 2px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                        borderRadius: 2,
                                    },
                                },
                            }}
                        />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Starting Point Coordinates
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            {gpxFiles.length > 0 ? 'Auto-filled from first GPX file; each upload uses its own GPX' : 'Will be auto-filled when you upload GPX files'}
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <TextField
                                type="text"
                                name="startLatitude"
                                placeholder="Start Latitude"
                                value={startLatitude}
                                onChange={(e) => setStartLatitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                type="text"
                                name="startLongitude"
                                placeholder="Start Longitude"
                                value={startLongitude}
                                onChange={(e) => setStartLongitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Ending Point Coordinates
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            {gpxFiles.length > 0 ? 'Auto-filled from first GPX file; each upload uses its own GPX' : 'Will be auto-filled when you upload GPX files'}
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <TextField
                                type="text"
                                name="endLatitude"
                                placeholder="End Latitude"
                                value={endLatitude}
                                onChange={(e) => setEndLatitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                type="text"
                                name="endLongitude"
                                placeholder="End Longitude"
                                value={endLongitude}
                                onChange={(e) => setEndLongitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Road Type 
                        </h1>
                        {roadId ? (
                            <TextField
                                type="text"
                                name="roadType"
                                fullWidth
                                value={roadType}
                                disabled={true}
                                variant="outlined"
                                style={{ backgroundColor: '#E5E7EB', width: '100%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <Select
                                value={roadType}
                                onChange={(value) => setRoadType(value)}
                                disabled={isPrepared}
                                placeholder="Select road type"
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    backgroundColor: '#F0F5FE'
                                }}
                            >
                                <Select.Option value="LHS">LHS</Select.Option>
                                <Select.Option value="RHS">RHS</Select.Option>
                                <Select.Option value="Service Road">Service Road</Select.Option>
                            </Select>
                        )}
                    </div>

                    {errorMessage && (
                        <div style={{ display: 'flex', alignItems: 'center', width: 'full', height: 'fit', textAlign: 'center', backgroundColor: '#FFEEEF', color: '#DF0028', padding: 4 }}>
                            <FiAlertCircle style={{ marginRight: 4 }} /> {errorMessage}
                        </div>
                    )}

                    {isUploading && (
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                <Progress
                                    status="active"
                                    strokeColor={{ from: '#108ee9', to: '#87d068' }}
                                    percent={uploadProgress}
                                />
                            </div>
                            {preparedItems.map((item, index) => (
                                <div key={item.uid} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '0.85rem', color: '#0A2540' }}>{item.video?.name || `Video ${index + 1}`}</span>
                                        <span style={{ fontSize: '0.8rem', color: '#5F6368' }}>{(perFileProgress[index] || 0)}%</span>
                                    </div>
                                    <Progress
                                        status="active"
                                        percent={perFileProgress[index] || 0}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button key="back" onClick={handleCancel} style={{ borderColor: '#F82D1C', color: '#F82D1C' }}>
                            Cancel
                        </Button>,
                        <Button key="submit" type="primary" onClick={loading ? null : handleSubmit}
                            style={{
                                backgroundColor: '#1967D2',
                                color: 'white',
                                alignItems: 'center',
                                display: 'flex',
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <Spin indicator={<LoadingOutlined spin />} size="small" style={{ color: 'white' }} />
                            ) : (
                                <MdOutlineDone size={18} style={{ marginRight: 4 }} />
                            )}
                            {loading ? (isUploading ? 'Uploading...' : 'Preparing...') : (isPrepared ? `Upload ${preparedItems.length} file(s)` : 'Prepare for upload')}
                        </Button>
                    </div>
                </div>
            </>
            <input
                type="file"
                accept="video/*"
                ref={videoInputRef}
                style={{ display: 'none' }}
                onChange={handleVideoFileChange}
                disabled={isPrepared}
                multiple
            />
            <input
                type="file"
                accept=".gpx"
                ref={gpxInputRef}
                style={{ display: 'none' }}
                onChange={handleGpxFileChange}
                disabled={isPrepared}
                multiple
            />
        </Modal>
    )
}

export default Upload
